import { Injectable, Provider } from '@angular/core';
import { ActivatedRoute, NavigationExtras } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NamedRouterOutletService } from '@y42/angular/router';
import { takePromise } from '@y42/util/take-promise';
import { distinctUntilChanged, map, Observable, of, shareReplay, switchMap } from 'rxjs';
import { SidebarComponentType, SIDEBAR_ROUTER_OUTLET_NAME } from './sidebar.model';

const SidebarComponentTypeToSkipLocationChange: Record<SidebarComponentType, boolean> = {
  [SidebarComponentType.Asset]: false,
  [SidebarComponentType.FileTree]: true,
};

export function provideSidebar(): Provider {
  return SidebarService;
}

@UntilDestroy()
@Injectable()
export class SidebarService {
  constructor(
    private readonly namedRouterOutletService: NamedRouterOutletService,
    private readonly route: ActivatedRoute,
  ) {}

  // !!! IMPORTANT !!!
  // The correct `relativeTo` `ActivatedRoute` object is the **key for correct navigation** in secondary router-outlets (sidebar, bottom_drawer).
  //
  // With the current route configuration and hierarchy in `feature.module` the correct `ActivatedRoute` object is the *parent* of the `FeatureComponent`.
  // If routing in the sidebar or bottom_drawer breaks play around with either `relativeTo.parent` or `firstChild` or `children`.
  readonly relativeTo = this.route.parent;

  private readonly routerOutletName = SIDEBAR_ROUTER_OUTLET_NAME;

  readonly isOpen$: Observable<boolean> = this.namedRouterOutletService
    .selectOutletActivated(this.routerOutletName)
    .pipe(untilDestroyed(this));

  selectIsSidebarTabActive(sidebarComponentType: SidebarComponentType): Observable<boolean> {
    return this.namedRouterOutletService
      .selectIsPathActive(this.routerOutletName, sidebarComponentType)
      .pipe(untilDestroyed(this));
  }

  selectSidebarTabActivatedRoute() {
    return this.namedRouterOutletService.selectChildActivatedRoute(this.routerOutletName).pipe(untilDestroyed(this));
  }

  selectRouteParam(paramName: string): Observable<string | null> {
    return this.namedRouterOutletService.selectChildActivatedRoute(this.routerOutletName).pipe(
      switchMap((route) => route?.params || of({})),
      map((params) => params?.[paramName] || null),
      distinctUntilChanged(),
      untilDestroyed(this),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  async toggle(sidebarComponentType: SidebarComponentType, param?: string) {
    const sidebarComponentTypeOpen = await takePromise(this.selectIsSidebarTabActive(sidebarComponentType));
    if (sidebarComponentTypeOpen) {
      await this.close();
    } else {
      await this.open(sidebarComponentType, param);
    }
  }

  async open(sidebarComponentType: SidebarComponentType, param?: string | string[], queryParams?: object) {
    let commands: string[] = [sidebarComponentType];
    const skipLocationChange = SidebarComponentTypeToSkipLocationChange[sidebarComponentType];

    if (param) {
      if (Array.isArray(param)) {
        commands = [...commands, ...param];
      } else {
        commands = [...commands, param];
      }
    }

    await this.navigate(commands, {
      skipLocationChange,
      queryParams,
    });
  }

  async close() {
    await this.navigate(null);
  }

  private async navigate(commands: string[] | null, options?: NavigationExtras) {
    this.namedRouterOutletService.navigate(this.routerOutletName, commands, {
      relativeTo: this.relativeTo,
      ...(options || {}),
    });
  }
}
